import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChartOptions, TooltipItem } from 'chart.js';
import { Line } from 'react-chartjs-2';
import CameraIcon from 'assets/icons/camera.svg?react';
import CarIcon from 'assets/icons/car.svg?react';
import CashIcon from 'assets/icons/cash.svg?react';
import SpaceForceIcon from 'assets/space-force.svg?react';
import lineGraphImg from 'assets/lineGraph.png';
import AGSimpleSelect from 'components/AGSelect/AGSimpleSelect/AGSimpleSelect';
import EmptyWidget from 'components/EmptyWidget/EmptyWidget';
import LineChartSkeleton from 'components/Skeletons/LineChartSkeleton';
import RectangleSkeleton from 'components/Skeletons/RectangleSkeleton';
import WidgetErrorMessage from 'components/WidgetErrorMessage/WidgetErrorMessage';
import WidgetBox from 'components/widgets/WidgetBox';
import { trackingClient } from 'clients';
import { HTTPError } from 'clients/HTTPClient';
import { getMockEnforcementStats } from 'mocks/enforcementStats';
import { EnforcementStats } from 'types/CarTracking';
import KpiCard from '../KpiCard/KpiCard';
import {
    getAutostartRevenueDisplay,
    getEnforcementScansChartData,
    getEnforcementStatsGranularity,
    getLookbackLabel,
    scansLookbackOptions,
    getScansSinceTimestamp,
    hasRenderableScansData,
    ScansLookbackPeriod,
} from './enforcementScansHelpers';
import styles from './EnforcementScansWidget.module.css';

type EnforcementScansWidgetProps = {
    spotUuid: string;
    isDemoMode?: boolean;
};

const DEFAULT_LOOKBACK_PERIOD: ScansLookbackPeriod = '4w';

function EnforcementScansLoadingSkeleton(): JSX.Element {
    return (
        <div className={styles.skeletonContainer} data-testid="enforcement-scans-skeleton">
            <div className={styles.skeletonTitle}>
                <RectangleSkeleton width="96px" height="16px" />
            </div>
            <div className={styles.skeletonSummaryRow}>
                <div className={styles.skeletonKpiRow}>
                    {['plates', 'autostarts', 'revenue'].map((id) => (
                        <div key={id} className={styles.skeletonKpiCard}>
                            <RectangleSkeleton width="120px" height="12px" />
                            <div className={styles.skeletonKpiValueRow}>
                                <div className={styles.skeletonIcon}>
                                    <RectangleSkeleton />
                                </div>
                                <div className={styles.skeletonKpiText}>
                                    <RectangleSkeleton width="84px" height="16px" />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                <div className={styles.skeletonFilter}>
                    <RectangleSkeleton width="140px" height="36px" />
                </div>
            </div>
            <div className={styles.skeletonChart}>
                <LineChartSkeleton />
            </div>
        </div>
    );
}

function EnforcementScansWidget({ spotUuid, isDemoMode = false }: EnforcementScansWidgetProps): JSX.Element {
    const [lookbackPeriod, setLookbackPeriod] = useState<ScansLookbackPeriod>(DEFAULT_LOOKBACK_PERIOD);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [report, setReport] = useState<EnforcementStats | null>(null);

    const fetchEnforcementStats = useCallback(async () => {
        if (!spotUuid) {
            return;
        }

        setLoading(true);
        setErrorMessage('');
        try {
            const granularity = getEnforcementStatsGranularity(lookbackPeriod);
            const response = isDemoMode
                ? getMockEnforcementStats(lookbackPeriod)
                : await trackingClient.getEnforcementStats(
                      spotUuid,
                      getScansSinceTimestamp(lookbackPeriod),
                      granularity
                  );
            setReport(response);
        } catch (error) {
            if (HTTPError.isHTTPError(error)) {
                setErrorMessage(error.message);
            } else {
                setErrorMessage(error?.toString() || 'Something went wrong');
            }
        } finally {
            setLoading(false);
        }
    }, [isDemoMode, lookbackPeriod, spotUuid]);

    useEffect(() => {
        fetchEnforcementStats();
    }, [fetchEnforcementStats]);

    useEffect(() => {
        if (!scansLookbackOptions.some((option) => option.value === lookbackPeriod)) {
            setLookbackPeriod(DEFAULT_LOOKBACK_PERIOD);
        }
    }, [lookbackPeriod, scansLookbackOptions]);

    const chartData = useMemo(() => {
        if (!report) return null;
        return getEnforcementScansChartData(report, lookbackPeriod);
    }, [lookbackPeriod, report]);

    const isLoading = loading || !report;
    const showEmptyState =
        !!report && (!hasRenderableScansData(report, lookbackPeriod) || !chartData || chartData.labels.length === 0);
    const shouldRenderChart = report && chartData && chartData.labels.length > 0 && !showEmptyState;

    const options: ChartOptions<'line'> = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
                mode: 'index',
                intersect: false,
            },
            scales: {
                x: {
                    ticks: {
                        maxRotation: 0,
                        autoSkip: true,
                        maxTicksLimit: chartData?.xAxisMaxTicksLimit ?? 6,
                    },
                    title: {
                        display: !!chartData?.xAxisTitle,
                        text: chartData?.xAxisTitle,
                    },
                    grid: {
                        display: false,
                    },
                },
                y: {
                    min: 0,
                    max: chartData?.yAxisMax,
                    grid: {
                        display: true,
                        borderDash: [2],
                    },
                    ticks: {
                        stepSize: chartData?.yAxisStep,
                    },
                },
            },
            plugins: {
                legend: {
                    display: false,
                },
                tooltip: {
                    callbacks: {
                        title(tooltipItems: TooltipItem<'line'>[]): string {
                            const tooltipItem = tooltipItems[0];
                            if (!tooltipItem) return '';

                            return chartData?.tooltipTitles[tooltipItem.dataIndex] ?? tooltipItem.label;
                        },
                        label(tooltipItem: TooltipItem<'line'>): string {
                            return `${tooltipItem.dataset.label} - ${tooltipItem.formattedValue}`;
                        },
                    },
                },
            },
        }),
        [chartData]
    );

    const content = useMemo(() => {
        if (errorMessage) {
            return <WidgetErrorMessage message={errorMessage} onRetry={fetchEnforcementStats} />;
        }

        if (isLoading) {
            return <EnforcementScansLoadingSkeleton />;
        }

        if (showEmptyState) {
            return (
                <div className={styles.content}>
                    <h4 className={styles.sectionTitle}>Today</h4>
                    <div className={styles.kpiRow}>
                        <KpiCard
                            icon={<CameraIcon />}
                            title="License Plate Scans"
                            value={<span className={styles.noDataValue}>No data yet</span>}
                        />
                        <KpiCard
                            icon={<CarIcon />}
                            title="Total Autostarts"
                            value={<span className={styles.noDataValue}>No data yet</span>}
                        />
                        <KpiCard
                            icon={<CashIcon />}
                            title="AutoStart Revenue"
                            value={<span className={styles.noDataValue}>No data yet</span>}
                        />
                    </div>
                    <h4 className={styles.sectionTitle}>Scans from {getLookbackLabel(lookbackPeriod)}</h4>
                    <EmptyWidget imgUrl={lineGraphImg} message="Data will appear here once it's available." />
                </div>
            );
        }

        if (shouldRenderChart) {
            return (
                <div className={styles.content}>
                    <h4 className={styles.sectionTitle}>Today</h4>
                    <div className={styles.kpiRow}>
                        <KpiCard
                            icon={<CameraIcon />}
                            title="License Plate Scans"
                            value={report.total_scanned_plates_today}
                        />
                        <KpiCard
                            icon={<CarIcon />}
                            title="Total Autostarts"
                            value={report.total_enforcement_autostart_today}
                        />
                        <KpiCard
                            icon={<CashIcon />}
                            title="Autostart Revenue"
                            value={getAutostartRevenueDisplay(report)}
                        />
                    </div>

                    <h4 className={styles.sectionTitle}>Scans from {getLookbackLabel(lookbackPeriod)}</h4>

                    <div className={styles.chartContainer}>
                        <Line
                            data={{
                                labels: chartData.labels,
                                datasets: chartData.datasets,
                            }}
                            options={options}
                            className={styles.chart}
                        />
                    </div>

                    <div className={styles.legendRow}>
                        <div className={styles.legendItem}>
                            <span className={`${styles.legendSquare} ${styles.autostarts}`} />
                            Space Force Scans
                        </div>
                        <div className={styles.legendItem}>
                            <span className={`${styles.legendSquare} ${styles.rentals}`} />
                            Rentals
                        </div>
                    </div>
                </div>
            );
        }

        return null;
    }, [
        chartData,
        errorMessage,
        fetchEnforcementStats,
        isLoading,
        lookbackPeriod,
        options,
        report,
        shouldRenderChart,
        showEmptyState,
    ]);

    return (
        <WidgetBox
            title="Space Force Scans"
            leftElement={<SpaceForceIcon />}
            rightElement={
                <div className={styles.filterRow}>
                    <AGSimpleSelect
                        options={scansLookbackOptions}
                        selected={lookbackPeriod}
                        onSelect={(selection) => setLookbackPeriod(selection as ScansLookbackPeriod)}
                        width="160px"
                    />
                </div>
            }
        >
            <div className={styles.widgetBody} data-testid="enforcement-scans-widget">
                {content}
            </div>
        </WidgetBox>
    );
}

export default EnforcementScansWidget;
